// CloudKeeper Org Services Portal — Amazon VPC IPAM API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
// Mirrors accessAnalyzerApi.js / inspectorApi.js / macieApi.js: every call
// resolves to the {success,data} envelope.
//
// GENERATED FROM THE ROUTE TABLE IN backend/handlers/ipam_handlers.go AND FROM
// NOTHING ELSE — 49 own routes plus the shared roles trio = 52 functions, in
// registration order. A function pointing at a path the router does not serve is
// a 404 the UI renders as a generic failure, so the table is the contract: no
// route invented, no route omitted.
//
// portalFetch gates on `response.ok`, so the 201 that POST /config answers and
// the 202 the five asynchronous creates answer (POST /scopes, /pools,
// /pools/{id}/cidrs, /pools/{id}/allocations, /pools/{id}/shares) resolve exactly
// like a 200 — no call here needs a status special case. Any non-2xx instead
// REJECTS with an Error carrying the backend's `error` string as `err.message`,
// AND the HTTP status as `err.status` AND the parsed body as `err.body`
// (services/portalApi.js:110-113). A 404, a 409 and a 500 are therefore
// distinguishable on the thrown Error, and IpamPoolDetail.js:145 already depends
// on it — the 409 that refuses a pool delete carries a structured `blockers`
// array the page renders as a table. Where a specific status is part of an
// endpoint's contract rather than a fault, it is called out on that function.
//
// EVERY ROUTE HERE IS IMPLEMENTED. Thirteen of them once answered 501 while their
// service methods were being built — the two /config PUTs, /cost-preview,
// /summary, all six /inventory reads, /teardown/active, /refresh/active and
// /refresh/{jobId} — and this header went on saying so long after they landed.
// backend/handlers/ipam_handlers.go now serves all of them against a real service
// (TestIPAMNoRouteAnswersNotImplemented and TestIPAMFormerlyPendingRoutesAreWired
// hold that line), so no caller here needs a 501 special case and none has one.
// A stale "this answers 501" comment is worse than none: it invites the next
// reader to write a fallback for a status the backend no longer returns.
//
// SHAPE NOTE: this file uses the `BASE` prefix form (accessAnalyzerApi.js:19-20)
// rather than guarddutyApi.js's inlined full paths. The route prefix is the one
// string that has to agree with the Go route registration AND with the audit
// EndpointRegistry, and keeping exactly one copy of it means a rename cannot
// half-land across fifty-one call sites. It is never inlined into portalApi.js,
// which is CloudTrail's mistake.
import { portalFetch } from './portalApi';

const BASE = '/api/v1/securityhub/ipam';

// The exact text of services.ErrIpamNotConfigured (services/ipam_service.go:147),
// spelled ONCE here rather than re-spelled per page — an improvement on
// AccessAnalyzerDashboard.js:124, which re-spells its own and would silently stop
// matching the day the backend reworded it.
//
// Most reads never surface this: GET /config, /cost-preview, /refresh/active and
// /teardown/active answer 200 with data:null for an unconfigured tenant, and the
// list routes answer an empty page. It reaches a caller from the routes that do
// reject when unconfigured — GET /summary (404 by design) and the {id} lookups —
// and the service wraps it (`...: %w`), so match with `includes`, not `===`.
export const IPAM_NOT_CONFIGURED = 'VPC IPAM is not configured';

// Query string for the five keyset-paginated inventory reads. Not exported: it is
// a spelling detail of this file, not a contract.
//
// `cursor` is the whole point. Dropping it silently pins every page to the first,
// which is exactly the pagination bug this codebase has already shipped once (the
// 50-row findings ceiling). `limit` is passed through UNCLAMPED on purpose — the
// clamp lives server-side and delegates to services.shClampFindingsLimit, and a
// second spelling of it here is how that ceiling shipped in the first place.
const pageQuery = ({ cursor, limit } = {}) => {
  const qs = new URLSearchParams();
  if (cursor) qs.set('cursor', cursor);
  if (limit) qs.set('limit', String(limit));
  const q = qs.toString();
  return q ? `?${q}` : '';
};

// Query string for the (hostAccountId, region) pair the pre-flight takes and the
// two GET-ish quota routes now accept OPTIONALLY. Not exported for the same
// reason pageQuery is not: a spelling detail, not a contract.
//
// THESE PARAMETERS ARE CONSULTED ONLY BEFORE A CONFIG ROW EXISTS. The backend
// rule is that the config wins whenever there is one, so the wizard — which runs
// its host and quota steps before POST /config has written anything — always
// sends them, and the Settings screens, which by definition have a config, never
// do. Sending them post-config is harmless but meaningless; omitting them
// pre-config is what pins a customer with a raised limit to the documented
// default of 5.
//
// A MISSING VALUE IS OMITTED RATHER THAN SENT, for the reason spelled out at
// length on getIpamAddressHistory below: `new URLSearchParams({region: undefined})`
// serialises the literal STRING "undefined", which arrives at the backend as a
// present-but-nonsense value that sails past its own absent-field check. Absent
// has to arrive as absent. That bug has already cost one live AWS call here.
const hostQuery = ({ hostAccountId, region } = {}) => {
  const qs = new URLSearchParams();
  if (hostAccountId) qs.set('hostAccountId', hostAccountId);
  if (region) qs.set('region', region);
  const q = qs.toString();
  return q ? `?${q}` : '';
};

// --- 1-8: config and lifecycle ---

// RESOLVES with `data: null` for a tenant that has never enabled VPC IPAM —
// HTTP 200, not 404 (DD-33). That resolved null IS the setup-wizard signal, so
// IpamEntry branches on `data === null` rather than on a rejection; a 404 would
// reach the UI as an indistinguishable failure and render the red error card on
// the exact screen whose job is to offer the enable button.
//
// A rejection from this call is therefore a REAL fault (500), not the pre-enable
// state. Deliberately unlike getIpamSummary below, which does 404 when
// unconfigured — /config owns the enabled/disabled question.
export const getIpamConfig = () =>
  portalFetch(`${BASE}/config`);

// BEGIN, not enable. Creates no AWS resource and starts no meter: the row lands
// at awaiting_consent and nothing moves until activateIpamConfig passes the cost
// gate. Answers 201.
//
// body: { hostAccountId, memberAccountIds, homeRegion, operatingRegions }
// (services.IpamConfigRequest). realm and customer id come from the portal token,
// never from this body.
//
// A refusal can carry a structured `quota` object alongside `error` when the
// account's resource-discovery-association ceiling is too low — the wizard draws
// its quota-increase button from those numbers. portalFetch rejects with the
// message only, so the affordance needs the rejected response body; see
// getIpamQuota for the polling half of that flow.
export const beginIpamConfig = (body) =>
  portalFetch(`${BASE}/config`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// THE COST GATE. `costAcknowledged` must be an explicit true — the service
// enforces that first, before the status check and before any write, so an
// omitted field leaves the tenant not paying. Never default it here: two gates
// means two places to forget.
export const activateIpamConfig = (costAcknowledged) =>
  portalFetch(`${BASE}/config/activate`, {
    method: 'POST',
    body: JSON.stringify({ costAcknowledged }),
  });

// Disables an enabled tenant, or abandons an awaiting_consent draft — the service
// reads the config's own status to tell them apart, so the caller never has to.
//
// `mode` defaults to "graceful" server-side when omitted; the destructive
// alternative is not reachable from here at all, it goes through
// cascadeDeleteIpam after a teardown parks at awaiting_cascade.
export const disableIpam = (mode) =>
  portalFetch(`${BASE}/config`, {
    method: 'DELETE',
    body: JSON.stringify({ mode }),
  });

// `operatingRegions` is the same field name the enable request carries, because
// it is the same list: ModifyIpam rewrites the IPAM's operating Regions across
// every discovery. Dropping a Region here stops discovery in it.
export const updateIpamOperatingRegions = (operatingRegions) =>
  portalFetch(`${BASE}/config/regions`, {
    method: 'PUT',
    body: JSON.stringify({ operatingRegions }),
  });

// Post-enable tier change: "free" | "advanced", AWS's IpamTier values verbatim.
//
// It carries the SAME consent gate as activateIpamConfig because it is the same
// decision — this route is what moves the meter — and the Advanced→Free direction
// is the dangerous one: it is a long precondition chain that can fail late on
// "pools still exist", which is why the settings page captions it with its own
// progress copy rather than a generic "Updating".
export const updateIpamTier = (tier, costAcknowledged) =>
  portalFetch(`${BASE}/config/tier`, {
    method: 'PUT',
    body: JSON.stringify({ tier, costAcknowledged }),
  });

// The estimate plus `meteredAccount` / `meteredAccountLocked`.
//
// RESOLVES with `data: null` when there is nothing to estimate yet — the enable
// screen wants "we can't estimate this yet", not an error card. Same null
// contract as getIpamConfig, and for the same reason.
export const getIpamCostPreview = () =>
  portalFetch(`${BASE}/cost-preview`);

// REJECTS (HTTP 404, IPAM_NOT_CONFIGURED) for an unconfigured tenant, and that
// asymmetry with getIpamConfig is deliberate rather than an oversight: the
// headline numbers are noncompliant-CIDR and overlap counts, so a fabricated zero
// would make "IPAM is off" render identically to "no address-space conflicts
// anywhere in your org" — the most dangerous false reassurance this capability
// can produce.
//
// The rejection is left intact rather than mapped to null here; callers that want
// the wizard branch off getIpamConfig, which owns that state.
export const getIpamSummary = () =>
  portalFetch(`${BASE}/summary`);

// --- 9-12: members ---
//
// There is no /ipam/accounts route: the enrollment picker is the shared
// GET /api/v1/securityhub/accounts that already serves every capability.

// One row per (account × Region) cell, carrying Prism's own phase AND AWS's
// ResourceDiscoveryStatus / FailureCode — the Accounts page needs both axes.
//
// Answers `data: []` for an un-enabled tenant: an empty grid, deliberately
// distinct from the `null` that means "run the wizard".
export const listIpamMembers = () =>
  portalFetch(`${BASE}/members`);

export const addIpamMembers = (accountIds) =>
  portalFetch(`${BASE}/members`, {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });

// The account id travels in the BODY against a STATIC path, following Access
// Analyzer / Inspector / Macie rather than CSPM's POST /members/{accountId}/retry.
// That keeps the retry verb out of the wildcard family entirely, so no future POST
// on /members/{accountId} can ever swallow "retry" as an account id.
export const retryIpamMember = (accountId) =>
  portalFetch(`${BASE}/members/retry`, {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });

// Disassociate the discovery, delete the RAM share, delete the discovery, drop
// the grant — in that order, in the background. No VPC and no customer resource
// is touched.
export const removeIpamMember = (accountId) =>
  portalFetch(`${BASE}/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

// --- 13-16: scopes ---

// Includes the two scopes AWS auto-creates, flagged isDefault: true /
// prismManaged: false. Empty page, never an error, for an un-enabled tenant.
export const listIpamScopes = () =>
  portalFetch(`${BASE}/scopes`);

// body: { description } (services.IpamScopeRequest). Answers 202 with the ledger
// row at create-in-progress — AWS has not finished minting the scope yet, so the
// caller polls listIpamScopes rather than treating the response as terminal.
export const createIpamScope = (body) =>
  portalFetch(`${BASE}/scopes`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Description only — everything else about a scope is immutable in AWS.
export const updateIpamScope = (scopeId, body) =>
  portalFetch(`${BASE}/scopes/${encodeURIComponent(scopeId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// Non-default scopes only; a default is refused with a 400 that names why rather
// than with a raw AWS error.
export const deleteIpamScope = (scopeId) =>
  portalFetch(`${BASE}/scopes/${encodeURIComponent(scopeId)}`, {
    method: 'DELETE',
  });

// --- 17-28: pools, pool CIDRs, pool allocations ---

// Flat list with sourceIpamPoolId, so a caller can rebuild the hierarchy itself.
// `scopeId` is an OPTIONAL filter and not a key: the pools page opens unscoped and
// an omitted value means "every scope".
export const listIpamPools = (scopeId) => {
  const q = scopeId ? `?${new URLSearchParams({ scopeId }).toString()}` : '';
  return portalFetch(`${BASE}/pools${q}`);
};

// body: services.IpamPoolRequest — { scopeId, sourceIpamPoolId, description,
// addressFamily, locale, allocation*NetmaskLength, autoImport, ... }. Answers 202:
// the row comes back at create-in-progress by design.
export const createIpamPool = (body) =>
  portalFetch(`${BASE}/pools`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// The STATIC route, registered before /pools/{poolId} — "tree" is not a pool id.
// A pool the host can see but cannot place (null scope, i.e. not created by Prism)
// arrives under a synthetic "Unscoped" root rather than being dropped.
export const getIpamPoolTree = () =>
  portalFetch(`${BASE}/pools/tree`);

// 404 for a pool this tenant does not own, NEVER 403 — every read filters on realm
// AND customer id, so another tenant's pool id misses exactly like an id that
// never existed. Answering 403 for one and 404 for the other would turn this into
// an existence oracle for every pool id in the fleet.
export const getIpamPool = (poolId) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}`);

// body: services.IpamPoolUpdate — every field is a POINTER server-side, so an
// omitted key means "leave it alone" while an explicit null/zero means "set it".
// Send only what changed.
export const updateIpamPool = (poolId, body) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

// NEVER CASCADES. A pool holding a resource-backed allocation is refused with 409
// and the blockers NAMED on the response body ("delete vpc-0… in account 8576…"),
// because AWS provides no force-release and the operator's next action is to
// delete the resource, not to fix the request.
export const deleteIpamPool = (poolId) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}`, {
    method: 'DELETE',
  });

export const listIpamPoolCidrs = (poolId) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}/cidrs`);

// body: { cidr, netmaskLength? } (services.IpamPoolCidrRequest). Answers 202 —
// AWS returns pending-provision and the ledger row is written at that state, so
// the pool detail page polls listIpamPoolCidrs until it settles.
export const provisionIpamPoolCidr = (poolId, body) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}/cidrs`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// THE CIDR IS A SINGLE ENCODED PATH SEGMENT (DD-31): "10.100.0.0/16" becomes
// "10.100.0.0%2F16". encodeURIComponent is not cosmetic here — an unencoded slash
// splits the path, misses the single-segment {cidr} pattern and 404s every
// deprovision. It works because the router calls r.UseEncodedPath() (matching runs
// against the encoded path) and a router-level middleware PathUnescapes every mux
// var, so the handler reads the decoded value back.
//
// Same reason the DELETE-with-a-body alternative was rejected: it would be
// unroutable by template, invisible to the audit registry as a distinct action,
// and dropped by every intermediary that strips DELETE bodies.
export const deprovisionIpamPoolCidr = (poolId, cidr) =>
  portalFetch(
    `${BASE}/pools/${encodeURIComponent(poolId)}/cidrs/${encodeURIComponent(cidr)}`,
    { method: 'DELETE' }
  );

export const listIpamPoolAllocations = (poolId) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}/allocations`);

// body: { cidr, netmaskLength?, description } (services.IpamAllocationRequest).
//
// THERE IS NO REGION PARAMETER, here or on the release below, and its absence is
// structural: the allocation plane is locale-bound (AllocateIpamPoolCidr sent to
// the wrong Region fails), so the Region is a property of the pool row that the
// service derives. A caller-supplied Region could only ever be the same answer or
// a wrong one — and the wrong one would fail deep inside an AWS call instead of at
// the edge. The request struct has no Region field, so a "region" key added here
// would be silently discarded, which is worse than being refused.
export const allocateIpamPoolCidr = (poolId, body) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}/allocations`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Refused with 409 when the allocation is resource-backed — the message names the
// one resource. Same product boundary as deleteIpamPool: "delete the VPC", never
// "fix your request".
export const releaseIpamPoolAllocation = (poolId, allocationId) =>
  portalFetch(
    `${BASE}/pools/${encodeURIComponent(poolId)}/allocations/${encodeURIComponent(allocationId)}`,
    { method: 'DELETE' }
  );

// --- 29-33: RAM shares ---
//
// Pool shares are nested under /pools/{poolId}/shares rather than a flat
// /shares/{shareId}, which is what keeps them from colliding with
// /shares/invitations: both would have answered 200 in the same envelope, so a
// transposition could not have been caught by a status assertion at all.

export const listIpamPoolShares = (poolId) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}/shares`);

// `principals` are account ids, OU ARNs or organization ARNs
// (services.IpamPoolShareRequest). Answers 202: a share to a principal outside the
// organization lands as an UNACCEPTED INVITATION, so the row is non-terminal by
// design and the receiver has to accept it — see acceptIpamShareInvitation.
export const createIpamPoolShare = (poolId, principals) =>
  portalFetch(`${BASE}/pools/${encodeURIComponent(poolId)}/shares`, {
    method: 'POST',
    body: JSON.stringify({ principals }),
  });

// A RAM share ARN carries both ':' and '/', so — exactly like the CIDR above — it
// only survives as one path segment because of encodeURIComponent plus
// UseEncodedPath and the decode middleware (DD-31).
//
// `poolId` is in the path but the service does not read it: a share ARN is
// globally unique and the tenant scope is already the realm+customer pair. The
// segment stays because it is what makes the share belong to a pool in the URL
// space, and what keeps this route out of a flat /shares/{id} family.
export const deleteIpamPoolShare = (poolId, shareArn) =>
  portalFetch(
    `${BASE}/pools/${encodeURIComponent(poolId)}/shares/${encodeURIComponent(shareArn)}`,
    { method: 'DELETE' }
  );

// The repair screen. Unlike /members, /scopes and /pools this one has NO empty
// page contract: it is only reachable after enablement, so there is no pre-enable
// state to render and an unconfigured tenant is a genuine 404.
export const listIpamShareInvitations = () =>
  portalFetch(`${BASE}/shares/invitations`);

export const acceptIpamShareInvitation = (invitationArn) =>
  portalFetch(`${BASE}/shares/invitations/accept`, {
    method: 'POST',
    body: JSON.stringify({ invitationArn }),
  });

// --- 34-39: inventory ---
//
// 34-38 are keyset-paginated DB reads over Prism's own cache; each answers an
// EMPTY PAGE (`data: []`) for an unconfigured tenant, never an error, because they
// are list routes. See pageQuery for why `cursor` is plumbed through and why
// `limit` is not clamped here.
//
// 39 is the odd one out and is documented on its own function.

// The discovered address space — what IPAM FOUND. Together with
// listIpamInventoryAllocations (what IPAM HANDED OUT) this is the compliance
// story: a VPC created outside a pool appears in one and not the other.
export const listIpamDiscoveredCidrs = (params) =>
  portalFetch(`${BASE}/inventory/discovered-cidrs${pageQuery(params)}`);

// The (account × Region) discovery-health grid, carrying AWS's own typed health
// model — ResourceDiscoveryStatus, FailureCode, LastSuccessfulDiscoveryTime.
// LastSuccessfulDiscoveryTime is legitimately absent for an account whose first
// sweep has not landed (AWS takes up to ~20 minutes), so treat null as "not yet",
// not as an error.
export const listIpamDiscoveredAccounts = (params) =>
  portalFetch(`${BASE}/inventory/discovered-accounts${pageQuery(params)}`);

// Per-CIDR compliance / overlap / management state from the scope's own view.
export const listIpamResourceCidrs = (params) =>
  portalFetch(`${BASE}/inventory/resource-cidrs${pageQuery(params)}`);

// The CROSS-POOL allocation ledger. Distinct from listIpamPoolAllocations, which
// is one pool's rows hanging off an {id} lookup and 404s for a missing pool.
export const listIpamInventoryAllocations = (params) =>
  portalFetch(`${BASE}/inventory/allocations${pageQuery(params)}`);

// Public IP Insights — the idle-EIP / public-IPv4-cost dataset. Its backing AWS
// operation has NO SDK paginator, so the page must not assume a bounded first
// page: render a real "Load more" that feeds the cursor back through pageQuery.
export const listIpamPublicAddresses = (params) =>
  portalFetch(`${BASE}/inventory/public-addresses${pageQuery(params)}`);

// THE ONLY READ IN THIS CAPABILITY THAT DIALS AWS. There is no cache and there
// cannot be one: GetIpamAddressHistory is a per-CIDR lookup with no bulk feed, so
// a cache would have no bounded population strategy, and the data is destroyed
// irreversibly by DeleteIpam — the cache would outlive its source and start lying
// exactly when it matters (DD-35). It is a drawer, not a page.
//
// `cidr` and `scopeId` are BOTH REQUIRED: a missing one is a 400 raised before any
// AWS call. This is a lookup, not a list, so it has no empty-page contract and no
// cursor. Being live, it is also the one read a caller can throttle by mashing a
// button.
//
// A throttle does NOT come back as 429: the handler answers 500, because the
// throttle predicate is unexported on the service side and there is no status
// for it to map to. It is distinguishable by MESSAGE instead — the service wraps
// AWS's own text behind "AWS throttled this request (rate exceeded); …", which is
// what IpamHistoryDrawer matches on. `err.status` DOES reach the caller, so the
// reason not to branch on it here is not that it is missing — it is that on this
// route it says 500 for both a throttle and a genuine fault, and would therefore
// mislabel one of them.
//
// A missing value is omitted rather than sent, because
// `new URLSearchParams({cidr: undefined})` serialises the STRING "undefined" — the
// backend would then see a present-but-nonsense CIDR, skip its own required-field
// check and spend an AWS call on it. Absent has to arrive as absent.
export const getIpamAddressHistory = ({ cidr, scopeId } = {}) => {
  const qs = new URLSearchParams();
  if (cidr) qs.set('cidr', cidr);
  if (scopeId) qs.set('scopeId', scopeId);
  return portalFetch(`${BASE}/inventory/address-history?${qs.toString()}`);
};

// --- 40-42: teardown ---

// The pre-flight residue ledger: what a teardown WOULD delete and what it cannot.
// Rendered BEFORE any destructive button, so the operator sees the blockers first.
export const getIpamTeardownPreview = () =>
  portalFetch(`${BASE}/teardown-preview`);

// The live ipam_teardown_jobs row — stage, blockers, and whether it has parked at
// awaiting_cascade. RESOLVES with `data: null` when nothing is running (including
// for an unconfigured tenant, where "nothing is running" is exactly the truth),
// which is the idle state the settings page renders.
//
// Note the two spellings are different routes on purpose: /teardown-preview is a
// read of what teardown would do and exists before any job, while /teardown/* is
// the job namespace.
export const getIpamActiveTeardown = () =>
  portalFetch(`${BASE}/teardown/active`);

// THE SINGLE MOST DESTRUCTIVE ROUTE IN THE PORTAL: it deallocates CIDRs from live
// customer VPCs and destroys all CIDR address history irreversibly.
//
// `confirm` must be an explicit true — the service refuses a missing field rather
// than assuming it — and it is additionally refused unless a teardown job is
// already parked at awaiting_cascade, so it can never be the first call. Who
// confirmed is taken from the portal token, never from this body: it is the audit
// record of who destroyed a customer's IPAM.
export const cascadeDeleteIpam = (confirm) =>
  portalFetch(`${BASE}/teardown/cascade`, {
    method: 'POST',
    body: JSON.stringify({ confirm }),
  });

// --- 43-45: refresh (the three-part polling contract) ---

// `kind` is "discovery" | "scope_cidrs" | "public_addresses" | "pool_tree" |
// "all". An absent or unknown kind is normalised to "all" server-side rather than
// refused: a refresh control that answers 400 renders as a blank screen where a
// table should be.
//
// A double-clicked button is safe — StartRefresh RE-ATTACHES to an in-flight job
// of the same kind and returns it, instead of starting a competing fan-out that
// would double every AWS call in the run.
export const startIpamRefresh = (kind) =>
  portalFetch(`${BASE}/refresh`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });

// Currently running refresh job for this tenant, or `data: null` when there is
// none. This is what lets a dashboard re-attach its progress bar after navigation
// or reload. It is a STATIC route registered before /refresh/{jobId} — "active" is
// not a job id, and reversing that order 404s every mount of the progress bar.
export const getIpamActiveRefreshJob = () =>
  portalFetch(`${BASE}/refresh/active`);

// Rejects once a job ages out of the table, so poll loops should stop on rejection
// rather than retrying forever.
export const getIpamRefreshJob = (jobId) =>
  portalFetch(`${BASE}/refresh/${encodeURIComponent(jobId)}`);

// --- 46-49: the association-quota surface (DD-39) and the host pre-flight ---
//
// All four take the SAME optional (hostAccountId, region) pair, and they take it
// because the wizard asks these questions BEFORE POST /config has written a row.
// Without the parameters the quota routes had nothing to read the host account
// off, so they answered from the documented AWS default — capping a customer who
// had already raised their limit at four member accounts on their first enable,
// and disabling the very button that files the increase.

// THE HOST PRE-FLIGHT: everything the wizard must be able to say before it
// collects cost consent, in one read.
//
//   hostAccountId, region
//   associationCeiling, memberCeiling, ceilingIsLive
//   requestId, requestStatus, requestDesiredValue
//   hostIpamChecked, hostIpamExists, hostIpamId, hostIpamTier, hostIpamState
//   hostIpamDisposition   "none" | "ours" | "adoptable" | "blocking"
//   hostIpamRemedies      string[]
//
// `hostIpamChecked: false` MEANS THE DescribeIpams READ FAILED. It does not mean
// there is no IPAM, and nothing downstream may render it as one: "IPAMs per
// Region" (L-F8B4A9E6) is 1 and is NOT adjustable, so a false all-clear here
// sends the operator into a CreateIpam that AWS will refuse, after they have
// already accepted a bill. Every conflict statement on the wizard is gated on
// this flag being true.
//
// `hostIpamDisposition: "adoptable"` is the consent-changing one. It means the
// customer already owns an Advanced-tier IPAM in this account and Region, so
// Prism will ADOPT it rather than create one — and will never delete it, while
// every member discovery associated into it raises the customer's own bill on a
// resource Prism does not own the lifecycle of. That has to be said before the
// consent click, which is why it is a read of its own rather than a log line.
export const getIpamHostPreflight = ({ hostAccountId, region } = {}) =>
  portalFetch(`${BASE}/preflight${hostQuery({ hostAccountId, region })}`);

// The applied resource-discovery-association ceiling AND the tracked
// quota-increase request, on one payload.
//
// ENROLLMENT UNBLOCKS ON THE APPLIED CEILING, NEVER ON THE REQUEST STATUS: AWS
// moves a request to APPROVED before the applied value actually changes, so a UI
// that gated on the status string would wave the operator through into a
// half-enrollment with the meter already running.
export const getIpamQuota = (params) =>
  portalFetch(`${BASE}/quota${hostQuery(params)}`);

// `desiredValue` is the ceiling to ask AWS for — the refusal that triggers this
// flow carries it, so the wizard should send back the number it was given rather
// than one it computed.
//
// Host and Region travel in the BODY here rather than the query string, because
// this one is a POST and the backend reads them off the request struct. They are
// spread in only when present: an explicit `hostAccountId: undefined` would be
// dropped by JSON.stringify today, but a key that exists and means nothing is one
// refactor away from being serialised as null and failing a required-field check
// on the server that an absent key would have passed.
export const requestIpamQuotaIncrease = (desiredValue, { hostAccountId, region } = {}) =>
  portalFetch(`${BASE}/quota/request`, {
    method: 'POST',
    body: JSON.stringify({
      desiredValue,
      ...(hostAccountId ? { hostAccountId } : {}),
      ...(region ? { region } : {}),
    }),
  });

// The "Re-check now" affordance. It resolves to the same payload as getIpamQuota
// on purpose: reading the status already re-reads the applied ceiling from Service
// Quotas and reconciles the tracked request, so there is no separate "force" to
// implement. It is a distinct ROUTE rather than a re-hit of the GET because the
// two are different actions to an auditor and to a rate limiter — one is a page
// load, the other is a human pressing a button against an AWS API.
export const recheckIpamQuota = (params) =>
  portalFetch(`${BASE}/quota/recheck${hostQuery(params)}`, { method: 'POST' });

// --- role permissions ("Fix roles", props for RolePermissionsPanel) ---
// Prism keeps a scoped inline policy on each enrolled account's switch role. The
// scan is async: POST starts (or re-joins) a job, GET polls it. Fix is
// fire-and-forget — re-run a scan to see the outcome.
//
// These three are the shared securityhub roles routes registered under this
// capability's prefix by the 8th setupRolesRoutes call site, so they take the same
// shape as Inspector's and Access Analyzer's and can be handed to
// RolePermissionsPanel ({ startScan, getJob, fix, capabilityLabel }) unchanged.

export const startIpamRolesScan = () =>
  portalFetch(`${BASE}/roles/status`, { method: 'POST' });

export const getIpamRolesJob = (jobId) =>
  portalFetch(`${BASE}/roles/status/${encodeURIComponent(jobId)}`);

export const fixIpamRoles = () =>
  portalFetch(`${BASE}/roles/fix`, { method: 'POST' });
